<template>
  <div v-if="show" class="modal-backdrop fade show" @click="handleBackdropClick"></div>

  <div v-if="show" class="modal fade show" style="display: block;" tabindex="-1">
    <div class="modal-dialog modal-lg modal-dialog-scrollable">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Edit {{ componentTypeName }}</h5>
          <button type="button" class="btn-close" @click="handleClose"></button>
        </div>

        <div class="modal-body">
          <ul class="nav nav-tabs mb-3">
            <li class="nav-item">
              <button
                type="button"
                class="nav-link"
                :class="{ active: activeTab === 'general' }"
                @click="activeTab = 'general'"
              >
                <span class="badge" :class="activeTab === 'general' ? 'tab-badge-active' : 'tab-badge'">General</span>
              </button>
            </li>
            <li class="nav-item">
              <button
                type="button"
                class="nav-link"
                :class="{ active: activeTab === 'design' }"
                @click="activeTab = 'design'"
              >
                <span class="badge" :class="activeTab === 'design' ? 'tab-badge-active' : 'tab-badge'">Design</span>
              </button>
            </li>
            <li class="nav-item">
              <button
                type="button"
                class="nav-link"
                :class="{ active: activeTab === 'logic' }"
                @click="activeTab = 'logic'"
              >
                <span class="badge" :class="activeTab === 'logic' ? 'tab-badge-active' : 'tab-badge'">Logic</span>
              </button>
            </li>
            <li class="nav-item">
              <button
                type="button"
                class="nav-link"
                :class="{ active: activeTab === 'extras' }"
                @click="activeTab = 'extras'"
              >
                <span class="badge" :class="activeTab === 'extras' ? 'tab-badge-active' : 'tab-badge'">Extras</span>
              </button>
            </li>
          </ul>

          <div v-if="localComponent" v-show="activeTab === 'general'">
            <GeneralTab
              :component="localComponent"
              @update="handleUpdate"
            />
          </div>

          <div v-if="localComponent" v-show="activeTab === 'design'">
            <DesignTab
              :component="localComponent"
              @update="handleUpdate"
            />
          </div>

          <div v-if="localComponent" v-show="activeTab === 'logic'">
            <LogicTab
              :component="localComponent"
              @update="handleUpdate"
            />
          </div>

          <div v-if="localComponent" v-show="activeTab === 'extras'">
            <ExtrasTab
              :component="localComponent"
              @update="handleUpdate"
            />
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="handleClose">Cancel</button>
          <button type="button" class="btn btn-primary" @click="handleSave">Save Changes</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { FormComponent, TabType } from '../types/form-builder';
import GeneralTab from './tabs/GeneralTab.vue';
import DesignTab from './tabs/DesignTab.vue';
import LogicTab from './tabs/LogicTab.vue';
import ExtrasTab from './tabs/ExtrasTab.vue';

interface Props {
  show: boolean;
  component: FormComponent | null;
}

interface Emits {
  (e: 'close'): void;
  (e: 'save', component: FormComponent): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const activeTab = ref<TabType>('general');
const localComponent = ref<FormComponent | null>(null);

const componentTypeName = computed(() => {
  if (!localComponent.value) return '';

  if (localComponent.value.type === 'element') {
    return localComponent.value.elementType.charAt(0).toUpperCase() +
           localComponent.value.elementType.slice(1);
  }

  return localComponent.value.type.charAt(0).toUpperCase() +
         localComponent.value.type.slice(1);
});

watch(() => props.component, (newComponent) => {
  if (newComponent) {
    localComponent.value = JSON.parse(JSON.stringify(newComponent)) as FormComponent;
    activeTab.value = 'general';
  }
}, { immediate: true });

function handleUpdate(updates: Partial<FormComponent>) {
  if (localComponent.value) {
    localComponent.value = { ...localComponent.value, ...updates } as FormComponent;
  }
}

function handleSave() {
  if (localComponent.value) {
    emit('save', localComponent.value);
  }
}

function handleClose() {
  emit('close');
}

function handleBackdropClick(event: MouseEvent) {
  if (event.target === event.currentTarget) {
    handleClose();
  }
}
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

.modal {
  z-index: 1050;
}

.nav-link {
  cursor: pointer;
  border: none;
  background: none;
}

/* Tab badge styling */
.tab-badge {
  background-color: #e9ecef;
  color: #495057;
  font-weight: 500;
  padding: 0.35em 0.65em;
}

.tab-badge-active {
  background-color: #3B9796;
  color: #fff;
  font-weight: 500;
  padding: 0.35em 0.65em;
}

.nav-link:hover .tab-badge {
  background-color: #3B9796;
  color: #fff;
}
</style>
